import React, { useState } from 'react';
import { TonConnectUIProvider } from '@tonconnect/ui-react';
import TONWalletConnect from '../components/TONWalletConnect';
import TONBalance from '../components/TONBalance';
import TONTransactionHistory from '../components/TONTransactionHistory';
import STONSwap from '../components/STONSwap';
import STONPools from '../components/STONPools';
import Portfolio from '../components/Portfolio';
import PriceChart from '../components/PriceChart';
import TradeHistory from '../components/TradeHistory';
import Notifications from '../components/Notifications';
import TokenTracker from '../components/TokenTracker';
import TokenTrackerExtended from '../components/TokenTrackerExtended';
import EnhancedTransactionMonitor from '../components/EnhancedTransactionMonitor';
import TradeAnalytics from '../components/TradeAnalytics';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Wallet, History, Settings, TrendingUp, PieChart, Bell, Search, Activity, BarChart3 } from 'lucide-react';

const TONDashboard = () => {
  const [selectedAddress, setSelectedAddress] = useState('');

  // TON Connect manifest URL (should be hosted on your domain)
  const manifestUrl = `${window.location.origin}/tonconnect-manifest.json`;

  return (
    <TonConnectUIProvider manifestUrl={manifestUrl}>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <div className="container mx-auto px-4 py-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              TON Blockchain Dashboard
            </h1>
            <p className="text-gray-600 mt-2">
              Manage your TON wallets and track transactions on STON.fi DEX
            </p>
          </div>

          {/* Main Content */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Wallet Connect */}
            <div className="lg:col-span-2 space-y-6">
              <TONWalletConnect userId="demo_user" />

              {/* Quick Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-gray-600">
                      Network
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold">Testnet</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-gray-600">
                      Status
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-2">
                      <div className="h-3 w-3 bg-green-500 rounded-full animate-pulse"></div>
                      <p className="text-xl font-bold">Online</p>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-gray-600">
                      DEX
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold">STON.fi</p>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Right Column - Balance */}
            <div className="space-y-6">
              <TONBalance address={selectedAddress} />
            </div>
          </div>

          {/* Tabs Section */}
          <div className="mt-8">
            <Tabs defaultValue="tracker" className="w-full">
              <TabsList className="grid w-full grid-cols-9">
                <TabsTrigger value="tracker" className="flex items-center gap-2">
                  <Search className="h-4 w-4" />
                  Token
                </TabsTrigger>
                <TabsTrigger value="portfolio" className="flex items-center gap-2">
                  <PieChart className="h-4 w-4" />
                  Portfolio
                </TabsTrigger>
                <TabsTrigger value="swap" className="flex items-center gap-2">
                  <TrendingUp className="h-4 w-4" />
                  Swap
                </TabsTrigger>
                <TabsTrigger value="charts" className="flex items-center gap-2">
                  <TrendingUp className="h-4 w-4" />
                  Charts
                </TabsTrigger>
                <TabsTrigger value="monitor" className="flex items-center gap-2">
                  <Activity className="h-4 w-4" />
                  Monitor
                </TabsTrigger>
                <TabsTrigger value="trades" className="flex items-center gap-2">
                  <History className="h-4 w-4" />
                  Trades
                </TabsTrigger>
                <TabsTrigger value="analytics" className="flex items-center gap-2">
                  <BarChart3 className="h-4 w-4" />
                  Analytics
                </TabsTrigger>
                <TabsTrigger value="notifications" className="flex items-center gap-2">
                  <Bell className="h-4 w-4" />
                  Alerts
                </TabsTrigger>
                <TabsTrigger value="settings" className="flex items-center gap-2">
                  <Settings className="h-4 w-4" />
                  Settings
                </TabsTrigger>
              </TabsList>

              <TabsContent value="tracker" className="mt-6">
                <TokenTrackerExtended />
              </TabsContent>

              <TabsContent value="portfolio" className="mt-6">
                <Portfolio userId="demo_user" />
              </TabsContent>

              <TabsContent value="swap" className="mt-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <STONSwap walletAddress={selectedAddress} />
                  <STONPools />
                </div>
              </TabsContent>

              <TabsContent value="charts" className="mt-6">
                <div className="space-y-6">
                  <PriceChart token="TON" />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-sm">Market Stats</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">24h Volume</span>
                          <span className="font-medium">$1.2M</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Market Cap</span>
                          <span className="font-medium">$5.8B</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Circulating Supply</span>
                          <span className="font-medium">5.1B TON</span>
                        </div>
                      </CardContent>
                    </Card>
                    <TONBalance address={selectedAddress} />
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="monitor" className="mt-6">
                <EnhancedTransactionMonitor address={selectedAddress} limit={30} />
              </TabsContent>

              <TabsContent value="trades" className="mt-6">
                <TradeHistory userId="demo_user" limit={30} />
              </TabsContent>

              <TabsContent value="analytics" className="mt-6">
                <TradeAnalytics userId="demo_user" />
              </TabsContent>

              <TabsContent value="notifications" className="mt-6">
                <Notifications userId="demo_user" />
              </TabsContent>

              <TabsContent value="settings" className="mt-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Settings</CardTitle>
                    <CardDescription>
                      Configure your TON integration settings
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Active Wallet</label>
                      <input
                        type="text"
                        placeholder="Enter TON address"
                        value={selectedAddress}
                        onChange={(e) => setSelectedAddress(e.target.value)}
                        className="w-full px-3 py-2 border rounded-md font-mono text-sm"
                      />
                      <p className="text-xs text-gray-500">
                        Paste a TON address to view its balance and transactions
                      </p>
                    </div>

                    <div className="pt-4 border-t">
                      <p className="text-sm text-gray-600 mb-2">Network Information</p>
                      <div className="space-y-1 text-xs">
                        <p><span className="font-medium">Network:</span> TON Testnet</p>
                        <p><span className="font-medium">DEX:</span> STON.fi</p>
                        <p><span className="font-medium">API:</span> Backend Service</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </TonConnectUIProvider>
  );
};

export default TONDashboard;
